import { useMutation, useQueryClient } from 'react-query'
import cn from 'classnames'

import { Button } from '@components/Buttons'
import { Bookmark2Icon } from '@components/icons/Bookmark2Icon'
import { UnbookmarkIcon } from '@components/icons/UnbookmarkIcon'
import { useBookmarkAction } from '@hooks/bookmark/useBookmarkAction'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAlert } from '@hooks/useAlert'
import { useDebounce } from '@hooks/useDebounce'
import { BookmarkButtonProps } from './interface'

export function BookmarkButton({
  bookId,
  chapterId,
  isLightTheme,
  isNightTheme,
  isBookmarked,
  isSepiaTheme,
  isDarkTheme,
  isAuthenticated,
}: BookmarkButtonProps) {
  const { debounce } = useDebounce()
  const alert = useAlert()
  const loginModal = useModal({
    modal: 'login',
  })
  const bookmarkClient = useBookmarkAction()
  const queryClient = useQueryClient()
  function mutateBookmarkStatus(status: boolean) {
    queryClient.setQueryData<boolean>(
      ['story', bookId, 'chapter', chapterId, 'bookmarked'],
      () => status
    )
  }

  const { mutate: createBookmark, isLoading: isCreateBookmarkLoading } =
    useMutation((id: number) => bookmarkClient.createBookmark(id), {
      onSuccess: () => {
        mutateBookmarkStatus(true)
        alert.success('เพิ่มหนังสือเข้าที่คั่นหนังสือสำเร็จ')
      },
      onError: () => {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      },
    })

  const { mutate: removeBookmark, isLoading: isRemoveBookmarkLoading } =
    useMutation((id: number) => bookmarkClient.removeBookmarkByChapterId(id), {
      onSuccess: () => {
        alert.success('ลบที่คั่นหนังสือสำเร็จ')
        mutateBookmarkStatus(false)
      },
      onError: () => {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      },
    })

  function handleCreateBookmark() {
    if (isAuthenticated && chapterId) {
      if (isBookmarked) removeBookmark(chapterId)
      else createBookmark(chapterId)
    } else {
      loginModal.show()
    }
  }

  return (
    <Button
      type='button'
      className={cn(
        'tablet-mobile:w-[30px] tablet-mobile:h-[30px] tablet-mobile:rounded-full tablet-mobile:p-0 font-medium text-[14px] border dark:bg-dark-9 dark:text-dark-10 dark:border-dark-3',
        {
          'border-gray-4 bg-gray-4 text-secondary':
            isLightTheme && !isBookmarked,
          'border-white bg-white text-secondary': isSepiaTheme && !isBookmarked,
          'border-primary bg-white text-primary':
            (isLightTheme || isSepiaTheme) && isBookmarked,
          '!border-dark-3 !bg-dark-3 !text-gray-16':
            isDarkTheme && !isBookmarked,
          '!border-dark-3 !bg-dark-3 !text-dark-10':
            isNightTheme && !isBookmarked,
          '!border-dark-4 !bg-dark-4 !text-primary':
            (isDarkTheme || isNightTheme) && isBookmarked,
        }
      )}
      onClick={() => debounce(handleCreateBookmark, 400)}
      disabled={isCreateBookmarkLoading || isRemoveBookmarkLoading}
    >
      <div className='flex items-center space-x-[5px]'>
        {isBookmarked ? <Bookmark2Icon /> : <UnbookmarkIcon />}
        <span className='tablet-mobile:hidden'>คั่นหนังสือ</span>
      </div>
    </Button>
  )
}
